import { ReactNode } from "react";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

interface ReportCardProps {
  title: string;
  description: string;
  icon: ReactNode;
  onClick: () => void;
  status?: "available" | "coming-soon" | "beta";
  preview?: ReactNode;
  className?: string;
}

export function ReportCard({
  title,
  description,
  icon,
  onClick,
  status = "available",
  preview,
  className,
}: ReportCardProps) {
  const isAvailable = status === "available" || status === "beta";

  return (
    <div
      onClick={isAvailable ? onClick : undefined}
      className={cn(
        "group relative rounded-lg border bg-card p-6 transition-all flex flex-col min-h-[280px]",
        isAvailable && "cursor-pointer hover:bg-muted/50 hover:shadow-md",
        !isAvailable && "opacity-60 cursor-not-allowed",
        className
      )}
    >
      {/* Status Badge */}
      {status === "beta" && (
        <span className="absolute top-3 right-3 text-[10px] font-medium px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300">
          Beta
        </span>
      )}
      {status === "coming-soon" && (
        <span className="absolute top-3 right-3 text-[10px] font-medium px-2 py-0.5 rounded-full bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400">
          Coming Soon
        </span>
      )}

      {/* Header */}
      <div className="flex items-start gap-4">
        {/* Icon */}
        <div className="flex-shrink-0 h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
          {icon}
        </div>

        {/* Title & Description */}
        <div className="flex-1 min-w-0">
          <h3 className="text-base font-semibold leading-tight">{title}</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            {description}
          </p>
        </div>
      </div>

      {/* Preview Area - flexible content area */}
      {preview && (
        <div className="mt-4 flex-1 flex items-center justify-center">
          {preview}
        </div>
      )}

      {/* Footer */}
      <div className="mt-auto pt-4 flex items-center justify-between border-t">
        <span className="text-sm text-muted-foreground">
          {isAvailable ? "Click to view report" : "Coming soon"}
        </span>
        {isAvailable && (
          <ChevronRight className="h-5 w-5 text-muted-foreground group-hover:text-foreground transition-colors" />
        )}
      </div>
    </div>
  );
}
